import { useState, useEffect, useRef } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  type User,
} from 'firebase/auth';
import { auth } from '../lib/firebase';

const ALLOWED_DOMAIN = 'tomandsawyer.com';
const ALLOWED_EMAILS = ['skemantix@gmail.com']; // Project owner/admins
const DOMAIN_ERROR = `Access restricted to ${ALLOWED_DOMAIN} accounts`;

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

const googleProvider = new GoogleAuthProvider();

function isAllowedEmail(email: string | null): boolean {
  if (!email) return false;
  return email.endsWith(`@${ALLOWED_DOMAIN}`) || ALLOWED_EMAILS.includes(email);
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  });
  // Track domain rejection to preserve error across sign-out
  const domainRejectedRef = useRef(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && !isAllowedEmail(user.email)) {
        // User signed in with non-tomandsawyer.com email - sign them out
        domainRejectedRef.current = true;
        await firebaseSignOut(auth);
        setState({
          user: null,
          loading: false,
          error: DOMAIN_ERROR,
        });
        return;
      }

      // If user is null after domain rejection, preserve the error
      if (!user && domainRejectedRef.current) {
        setState((prev) => ({
          user: null,
          loading: false,
          error: prev.error || DOMAIN_ERROR,
        }));
        return;
      }

      setState({ user, loading: false, error: null });
    });

    return () => unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    domainRejectedRef.current = false; // Reset on new sign-in attempt
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const result = await signInWithPopup(auth, googleProvider);
      // Double-check domain (in case hd parameter was bypassed)
      if (!isAllowedEmail(result.user.email)) {
        await firebaseSignOut(auth);
        setState({
          user: null,
          loading: false,
          error: `Access restricted to @${ALLOWED_DOMAIN} accounts`,
        });
        return;
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Sign in failed',
      }));
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  return {
    user: state.user,
    loading: state.loading,
    error: state.error,
    signInWithGoogle,
    signOut,
    isAuthenticated: !!state.user,
  };
}
